<template>
  <div class="position-picker-map" style="height: 100%;">
    <div ref="bgImage" class="bg-image" :style="`background-image: url(${mapPath})`"></div>
    <div style="position: relative; height: 100%;">
      <svg
        id="zoom"
        ref="zoomSvg"
        :viewBox="`0 0 ${originalImageWidth} ${originalImageHeight}`"
        xmlns="http://www.w3.org/2000/svg"
        height="100%"
        width="100%"
        :class="{ 'cursor-auto': !isDragging, 'cursor-grabbing': isDragging }"
      >
        <g
          class="g"
          @pointerdown="pointerdown"
          @pointermove="pointermove"
          @pointerup="pointerup"
        >
          <image :href="mapPath" class="map-image" width="100%" />
          <map-position
            v-for="position in positions"
            :key="position.id"
            :position="position"
            :map-image-aspect-ratio="mapImageAspectRatio"
            :selected-position-ids="selectedPositionIds"
            @select-position="selectPosition"
          >
          </map-position>
        </g>
      </svg>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

import * as d3 from 'd3'

import MapPosition from '@/components/onboarding/MapPosition.vue'

export default {
  name: 'PositionPickerMap',
  components: {
    MapPosition
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    positionsFilter: {
      type: Function,
      default: () => () => true
    },
    selectMultiple: {
      type: Boolean,
      default: true
    }
  },
  data: () => ({
    transform: null,
    k: 1,
    g: null,
    bg: null,
    svg: null,
    imageWidth: 1,
    imageHeight: 1,
    originalImageWidth: 1000,
    pointerDown: false,
    isDragging: false
  }),
  computed: {
    ...mapState(
      {
        isSavingPositions: state => state.onboarding.isSavingPositions,
        mapPath: state => state.position.mapPath
      }
    ),
    ...mapGetters(
      {
        activePositions: 'position/activePositions'
      }
    ),
    mapImageAspectRatio: {
      get: function () {
        return this.$store.state.sessionCache.mapImageAspectRatio
      },
      set: function (val) {
        this.$store.commit('sessionCache/SET_MAP_IMAGE_ASPECT_RATIO', val)
      }
    },
    selectedPositionIds: {
      get: function () {
        return this.value
      },
      set: function (val) {
        this.$emit('input', val)
      }
    },
    positions: function () {
      return this.activePositions.filter(this.positionsFilter)
    },
    isMobile: function () {
      return this.$vuetify.breakpoint.mdAndDown
    },
    originalImageHeight: function () {
      return this.originalImageWidth * this.mapImageAspectRatio
    }
  },
  methods: {
    selectPosition: function ({ position }) {
      if (this.selectedPositionIds.includes(position.id)) {
        this.selectedPositionIds = this.selectedPositionIds.filter(id => id !== position.id)
      } else {
        if (this.selectMultiple) {
          this.selectedPositionIds.push(position.id)
        } else {
          this.selectedPositionIds = [position.id]
        }
      }
    },
    pointerdown: function (event) {
      if (event.button === 2) {
        // högerklick ner
        this.isDragging = true // pointerDown
      }
    },
    pointermove: function () {
      // this.isDragging = this.pointerDown
    },
    pointerup: function (event) {
      if (event.button === 2) {
        // högerklick upp
        this.isDragging = false // pointerDown
      }
    },
    zoomed: function (event) {
      const { transform } = event
      if (isNaN(transform.k)) {
        return false
      }

      this.g.attr('transform', transform)
      this.g.attr('stroke-width', 1 / transform.k)
      this.k = transform.k
    },
    zoomIn: function () {
      this.transform.scaleBy(this.svg.transition().duration(200), 1.25)
    },
    zoomOut: function () {
      this.transform.scaleBy(this.svg.transition().duration(200), 0.8)
    },
    resetZoom: function () {
      this.transform.scaleTo(this.svg, 1)
      const x = this.imageWidth / 2
      const y = this.imageHeight / 2
      this.transform.translateTo(this.svg.transition().duration(400), x, y)
    },
    windowResize: function () {
      const image = this.$refs.zoomSvg.getElementsByClassName('g')[0].getBBox()
      this.imageHeight = Math.max(image.height, 1)
      this.imageWidth = Math.max(image.width, 1)
    },
    zoomToPosition: function (coordinates) {
      const x = coordinates.x * this.originalImageWidth
      const y = coordinates.y * this.originalImageHeight
      if (this.k <= 3.5 || this.k >= 6) {
        this.k = 3.5
        const t = {}
        t.k = this.k
        t.x = x
        t.y = y
      }
      setTimeout(() => {
        this.transform.scaleTo(this.svg, this.k)
        this.transform.translateTo(this.svg.transition().duration(400), x, y)
      }, !this.transform ? 200 : 0)
    },
    zoomToCategory: function (id) {
      if (id) {
        const positions = this.activePositions.filter(position => position.category.id === id)
        const top = Math.min.apply(Math, positions.map(function (position) { return position.map_y }))
        const bottom = Math.max.apply(Math, positions.map(function (position) { return position.map_y }))
        const left = Math.min.apply(Math, positions.map(function (position) { return position.map_x }))
        const right = Math.max.apply(Math, positions.map(function (position) { return position.map_x }))
        this.zoomToArea({ top, bottom, left, right })
      }
    },
    zoomToArea: function (boundaries) {
      const w = boundaries.right - boundaries.left
      const h = boundaries.bottom - boundaries.top
      let k = 1 / Math.max(w, h)
      k = Math.min(k, 4)

      setTimeout(() => {
        this.transform.scaleTo(this.svg, k)
        const x = (boundaries.right + boundaries.left) / 2 * this.originalImageWidth
        const y = (boundaries.bottom + boundaries.top) / 2 * this.originalImageHeight
        this.transform.translateTo(this.svg.transition().duration(600), x, y)
      }, !this.transform ? 200 : 0)
    }
  },
  mounted () {
    const mapImageElement = document.getElementsByClassName('map-image')[0]
    mapImageElement.addEventListener('load', (event) => {
      const h = mapImageElement.getBBox().height
      const w = mapImageElement.getBBox().width
      this.imageHeight = Math.max(h, 1)
      this.imageWidth = Math.max(w, 1)
      if (w !== 0) {
        this.mapImageAspectRatio = h / w
      }
    })

    this.$nextTick(() => {
      this.transform = d3.zoom()
        .scaleExtent([0.4, 8])
        .on('zoom', this.zoomed)
      const zoomElement = this.$el.querySelector('#zoom')
      const gElement = this.$el.querySelector('.g')
      this.svg = d3.select(zoomElement)
      this.g = d3.select(gElement)
      this.svg.call(this.transform)
    })
    window.addEventListener('resize', this.windowResize)
  },
  destroyed () {
    window.removeEventListener('resize', this.windowResize)
  }
}
</script>
